import React from 'react';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useTranslation } from '../hooks/useTranslation';
import { useBudget } from '../App';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const Dashboard = () => {
  const { t, isRTL } = useTranslation();
  const { 
    monthlyTransactions, 
    totalIncome, 
    totalExpenses, 
    balance, 
    currency, 
    transactions 
  } = useBudget();

  // Format currency
  const formatCurrency = (amount) => {
    const formattedAmount = new Intl.NumberFormat(isRTL ? 'ar-YE' : 'en-US').format(amount);
    return `${formattedAmount} ${t(`currencies.${currency}`)}`;
  };

  // Recent transactions (last 5)
  const recentTransactions = transactions.slice(0, 5);

  // Category spending data
  const categoryData = {};
  monthlyTransactions
    .filter(t => t.type === 'expense')
    .forEach(t => {
      categoryData[t.category] = (categoryData[t.category] || 0) + parseFloat(t.amount);
    });

  const categoryChartData = {
    labels: Object.keys(categoryData).map(cat => t(`categories.${cat}`)),
    datasets: [{
      data: Object.values(categoryData),
      backgroundColor: [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', 
        '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
      ],
    }]
  };

  // Monthly trend data (last 6 months)
  const getMonthlyData = () => {
    const months = [];
    const incomeData = [];
    const expenseData = [];
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthKey = date.toISOString().substring(0, 7);
      
      const monthTransactions = transactions.filter(t => t.date.startsWith(monthKey));
      const monthIncome = monthTransactions
        .filter(t => t.type === 'income')
        .reduce((sum, t) => sum + parseFloat(t.amount), 0);
      const monthExpense = monthTransactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => sum + parseFloat(t.amount), 0);
      
      months.push(date.toLocaleDateString(isRTL ? 'ar-YE' : 'en-US', { month: 'short' }));
      incomeData.push(monthIncome);
      expenseData.push(monthExpense);
    }
    
    return { months, incomeData, expenseData };
  };

  const { months, incomeData, expenseData } = getMonthlyData();

  const trendChartData = {
    labels: months,
    datasets: [
      {
        label: t('transaction.income'),
        data: incomeData,
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 2,
      },
      {
        label: t('transaction.expense'),
        data: expenseData,
        backgroundColor: 'rgba(239, 68, 68, 0.5)',
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 2,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="space-y-6 slide-in">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">
          {t('dashboard.monthly_summary')}
        </h2>
        <p className="text-gray-600">
          {new Date().toLocaleDateString(isRTL ? 'ar-YE' : 'en-US', { 
            year: 'numeric', 
            month: 'long' 
          })}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card text-center bg-gradient-to-r from-green-400 to-green-600 text-white">
          <h3 className="text-lg font-semibold mb-2">{t('dashboard.total_income')}</h3>
          <p className="text-2xl font-bold">{formatCurrency(totalIncome)}</p>
        </div>
        
        <div className="card text-center bg-gradient-to-r from-red-400 to-red-600 text-white">
          <h3 className="text-lg font-semibold mb-2">{t('dashboard.total_expenses')}</h3>
          <p className="text-2xl font-bold">{formatCurrency(totalExpenses)}</p>
        </div>
        
        <div className={`card text-center ${
          balance >= 0 
            ? 'bg-gradient-to-r from-blue-400 to-blue-600' 
            : 'bg-gradient-to-r from-orange-400 to-orange-600'
        } text-white`}>
          <h3 className="text-lg font-semibold mb-2">{t('dashboard.balance')}</h3>
          <p className="text-2xl font-bold">{formatCurrency(balance)}</p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Monthly Trend Chart */}
        <div className="card">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            {t('dashboard.monthly_trend')}
          </h3>
          <div className="chart-container">
            <Bar data={trendChartData} options={chartOptions} />
          </div>
        </div>

        {/* Category Spending Chart */}
        <div className="card">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            {t('dashboard.spending_by_category')}
          </h3>
          <div className="chart-container">
            {Object.keys(categoryData).length > 0 ? (
              <Doughnut 
                data={categoryChartData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: isRTL ? 'left' : 'right',
                    },
                  },
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <p>{isRTL ? 'لا توجد مصروفات هذا الشهر' : 'No expenses this month'}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">
            {t('dashboard.recent_transactions')}
          </h3>
          <Link to="/transactions" className="btn-primary">
            {t('buttons.filter')} →
          </Link>
        </div>
        
        {recentTransactions.length > 0 ? (
          <div className="space-y-3">
            {recentTransactions.map(transaction => (
              <div 
                key={transaction.id} 
                className={`transaction-item ${
                  transaction.type === 'income' ? 'transaction-income' : 'transaction-expense'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-gray-800">
                      {transaction.description || t(`categories.${transaction.category}`)}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t(`categories.${transaction.category}`)} • {transaction.date}
                    </p>
                  </div>
                  <div className="text-right rtl:text-left">
                    <p className={`font-bold ${
                      transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t(`transaction.${transaction.type}`)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p className="mb-4">
              {isRTL ? 'لا توجد معاملات بعد' : 'No transactions yet'}
            </p>
            <Link to="/add" className="btn-primary">
              {t('nav.add_transaction')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;